// src/components/Authentication/UserProfile.js

import React, { useEffect, useState, useContext } from "react";
import apis from "../../services/api";
import { ToastContext } from "../../contexts/ToastContext";
import Input from "../Common/Input";
import {
  FaEnvelope,
  FaLock,
  FaUserAstronaut,
  FaEye,
  FaEyeSlash,
  FaEdit,
  FaCalendarAlt,
  FaTimes,
  FaKey,
  FaSignOutAlt,
} from "react-icons/fa";
import { mapAuthErrors } from "../../utils/errorMapping";
import Header from "../Common/Header"; // Reusing Header
import Particles from "../Common/Particles"; // Reusing Particles

const UserProfile = () => {
  const { addToast } = useContext(ToastContext);

  const [profile, setProfile] = useState({
    username: "",
    email: "",
    role: "",
    userType: "",
    validFrom: "",
    validTo: "",
    registeredDate: "",
    avatar: "", // URL to avatar image
  });

  const [loading, setLoading] = useState(true);

  // Form states for resetting password
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false); // Loading state for password reset

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userProfile = await apis.Security.userProfile();
        setProfile({
          username: userProfile.username,
          email: userProfile.email,
          role: userProfile.role,
          userType: userProfile.userType,
          validFrom: userProfile.validFrom
            ? new Date(userProfile.validFrom).toLocaleDateString()
            : "N/A",
          validTo: userProfile.validTo
            ? new Date(userProfile.validTo).toLocaleDateString()
            : "N/A",
          registeredDate: userProfile.registeredDate
            ? new Date(userProfile.registeredDate).toLocaleDateString()
            : "N/A",
          avatar: userProfile.avatar || "",
        });
      } catch (error) {
        const errorMsg = mapAuthErrors(error);
        addToast(errorMsg, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Password Modal Handlers
  const openPasswordModal = () => {
    setNewPassword("");
    setConfirmPassword("");
    setPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
  };

const handlePasswordReset = async (e) => {
  e.preventDefault();
  if (!newPassword || !confirmPassword) {
    addToast("Please fill in all password fields.", "error");
    return;
  }
  if (newPassword !== confirmPassword) {
    addToast("Passwords do not match.", "error");
    return;
  }

  setLoadingPassword(true); // Start loading

  try {
    // Schedule reload BEFORE awaiting the API (like in logout all)
    setTimeout(() => {
      window.location.reload();
    }, 2500); // 2.5 seconds delay

    const response = await apis.Security.resetPassword({
      NewPassword: newPassword,
    });
    addToast(response.message, "success");
    closePasswordModal();
  } catch (error) {
    const errorMsg = mapAuthErrors(error);
    addToast(errorMsg, "error");
  } finally {
    setLoadingPassword(false); // End loading
  }
};

  // Logout all sessions handler
  const handleLogoutAllSessions = async () => {
    try {
      setTimeout(() => {
        window.location.reload();
      }, 5000); // 5-second delay
      const response = await apis.Security.logoutAllSessions();
      addToast(
        response.message || "All sessions have been removed successfully.",
        "success"
      );
    } catch (error) {
      const errorMsg = mapAuthErrors(error);
      addToast(errorMsg, "error");
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-primary to-secondary">
          <div className="text-xl text-accentBlue">Loading Profile...</div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <Header />

      {/* Profile Content */}
      <div className="min-h-screen flex flex-col items-center justify-center pt-8 pb-6 px-4 bg-gradient-to-b from-primary to-secondary relative overflow-hidden">
        {/* Particles */}
        <Particles count={40} />

        {/* Profile Card */}
        <div className="bg-glassOverlay backdrop-blur-md p-8 rounded-3xl shadow-xl w-full max-w-2xl text-white animate-fadeInSlow space-y-6 flex flex-col items-center">
          <div className="flex flex-col items-center">
            {/* Avatar */}
            <div className="relative">
              <img
                src={profile.avatar || "https://i.pravatar.cc/300"}
                alt="User Avatar"
                className="w-32 h-32 rounded-full object-cover border-4 border-accentBlue shadow-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://i.pravatar.cc/300?img=10";
                }}
              />
              <button
                className="absolute bottom-0 right-0 bg-accentBlue p-2 rounded-full hover:bg-accentBlueDark transition-colors duration-200"
                aria-label="Edit Avatar"
                onClick={() => {
                  addToast("Avatar editing is not implemented yet.", "info");
                }}
              >
                <FaEdit className="text-white" />
              </button>
            </div>

            {/* Username with User Type and Role */}
            <h2 className="text-3xl font-semibold mt-4 flex items-center justify-center">
              <FaUserAstronaut className="mr-2 text-accentOrange" />
              {profile.username}
              <span className="ml-2 text-accentOrange text-lg">
                ({profile.userType} {profile.role})
              </span>
            </h2>
          </div>

          {/* Email */}
          <div className="w-full flex items-center justify-center space-x-2">
            <FaEnvelope className="text-accentOrange mr-2" />
            <span>{profile.email}</span>
          </div>

          {/* Registered Date */}
          <div className="w-full flex items-center justify-center space-x-2">
            <FaCalendarAlt className="text-accentOrange mr-2" />
            <span>
              <strong>Registered:</strong> {profile.registeredDate}
            </span>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4 w-full">
            <button
              onClick={openPasswordModal}
              className="flex items-center justify-center bg-accentBlue text-white py-2 px-4 rounded-lg shadow-md hover:bg-accentBlueDark transition-colors duration-200 w-full md:w-auto"
              aria-label="Reset Password"
            >
              <FaKey className="mr-2" />
              Reset Password
            </button>
            <button
              onClick={handleLogoutAllSessions}
              className="flex items-center justify-center bg-red-600 text-white py-2 px-4 rounded-lg shadow-md hover:bg-red-500 transition-colors duration-200 w-full md:w-auto"
              aria-label="Logout All Sessions"
            >
              <FaSignOutAlt className="mr-2" />
              Logout All Sessions
            </button>
          </div>
        </div>

        {/* Reset Password Modal */}
        {passwordModalOpen && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-password-title"
          >
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black opacity-50"
              onClick={closePasswordModal}
              aria-label="Close Password Modal"
            ></div>

            {/* Modal Content */}
            <div className="relative bg-glassOverlay backdrop-blur-md p-6 rounded-lg shadow-lg w-full max-w-sm text-white animate-zoomIn">
              <button
                onClick={closePasswordModal}
                className="absolute top-2 right-2 text-gray-300 hover:text-white focus:outline-none"
                aria-label="Close Password Modal"
              >
                &times;
              </button>
              <h3
                id="reset-password-title"
                className="text-xl font-semibold mb-4 flex items-center"
              >
                <FaLock className="mr-2 text-accentOrange" />
                Reset Password
              </h3>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  icon={<FaLock className="text-accentOrange" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="text-gray-300 focus:outline-none"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  }
                  required
                  ariaLabel="New Password"
                />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  icon={<FaLock className="text-accentOrange" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="text-gray-300 focus:outline-none"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  }
                  required
                  ariaLabel="Confirm Password"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={closePasswordModal}
                    className="flex items-center bg-red-600 text-white py-2 px-4 rounded-lg shadow-md hover:bg-red-500 transition-colors duration-200 space-x-1"
                    aria-label="Cancel"
                  >
                    <FaTimes className="mr-1" />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    disabled={loadingPassword}
                    className={`flex items-center bg-accentBlue text-white py-2 px-4 rounded-lg shadow-md hover:bg-accentBlueDark transition-colors duration-200 space-x-1 ${
                      loadingPassword ? "cursor-not-allowed opacity-70" : ""
                    }`}
                    aria-label="Reset Password"
                  >
                    {loadingPassword ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 mr-1 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                        <span>Resetting...</span>
                      </>
                    ) : (
                      <>
                        <FaKey className="mr-1" />
                        <span>Reset</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserProfile;
